import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  Dialog,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { FormattedMessage } from 'react-intl';
import axiosConfig from 'src/Resources/axiosConfig';

export interface DocumentItem {
  id: number;
  ticketId?: number;
  ticketCommentId?: number;
  installationId?: number;
  scope: number;
  s3Key: string;
  originalName: string;
  contentType: string;
  sizeBytes: number;
  uploadedByUserId: number;
  createdAt: string;
}

interface DocumentListProps {
  ticketId?: number;
  ticketCommentId?: number;
  installationId?: number;
  refreshKey?: number;
  canDelete?: boolean;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImage(contentType: string): boolean {
  return contentType.startsWith('image/');
}

function getFileIcon(contentType: string) {
  if (contentType === 'application/pdf') return <PictureAsPdfIcon fontSize="small" color="error" />;
  return <InsertDriveFileIcon fontSize="small" />;
}

function DocumentList({
  ticketId,
  ticketCommentId,
  installationId,
  refreshKey = 0,
  canDelete = false
}: DocumentListProps) {
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [previews, setPreviews] = useState<Record<number, string>>({});
  const [expandedImage, setExpandedImage] = useState<string | null>(null);

  const fetchDocuments = () => {
    setLoading(true);
    axiosConfig
      .get('/GetDocuments', {
        params: { ticketId, ticketCommentId, installationId }
      })
      .then((res) => {
        if (Array.isArray(res.data)) setDocuments(res.data);
      })
      .catch(() => setDocuments([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDocuments();
  }, [ticketId, ticketCommentId, installationId, refreshKey]);

  // Load image thumbnails
  useEffect(() => {
    documents.forEach((doc) => {
      if (isImage(doc.contentType) && !previews[doc.id]) {
        axiosConfig
          .get('/DownloadDocument', {
            params: { id: doc.id },
            responseType: 'blob'
          })
          .then((res) => {
            const url = window.URL.createObjectURL(new Blob([res.data], { type: doc.contentType }));
            setPreviews((prev) => ({ ...prev, [doc.id]: url }));
          })
          .catch(() => {});
      }
    });
  }, [documents]);

  // Clean up blob URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(previews).forEach((url) => window.URL.revokeObjectURL(url));
    };
  }, []);

  const handleDownload = (doc: DocumentItem) => {
    if (previews[doc.id]) {
      const link = document.createElement('a');
      link.href = previews[doc.id];
      link.setAttribute('download', doc.originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      return;
    }
    axiosConfig
      .get('/DownloadDocument', {
        params: { id: doc.id },
        responseType: 'blob'
      })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', doc.originalName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {});
  };

  const handleDelete = (doc: DocumentItem) => {
    axiosConfig
      .delete('/DeleteDocument', { params: { id: doc.id } })
      .then(() => fetchDocuments())
      .catch(() => {});
  };

  if (loading) return null;
  if (documents.length === 0) return null;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        <FormattedMessage id="attachments" defaultMessage="Attachments" />
        <Chip label={documents.length} size="small" sx={{ ml: 1 }} />
      </Typography>
      <List dense disablePadding>
        {documents.map((doc) => (
          <ListItem
            key={doc.id}
            disableGutters
            sx={{ alignItems: 'flex-start', flexDirection: 'column', gap: 0.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {!isImage(doc.contentType) && (
                <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                  {getFileIcon(doc.contentType)}
                </Box>
              )}
              <ListItemText
                primary={doc.originalName}
                secondary={`${formatFileSize(doc.sizeBytes)} — ${new Date(doc.createdAt).toLocaleDateString()}`}
              />
              <Box sx={{ ml: 'auto', flexShrink: 0 }}>
                <IconButton size="small" onClick={() => handleDownload(doc)}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
                {canDelete && (
                  <IconButton size="small" onClick={() => handleDelete(doc)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
            {isImage(doc.contentType) && previews[doc.id] && (
              <Box
                component="img"
                src={previews[doc.id]}
                alt={doc.originalName}
                onClick={() => setExpandedImage(previews[doc.id])}
                sx={{
                  maxWidth: 200,
                  maxHeight: 150,
                  borderRadius: 1,
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': { opacity: 0.85 }
                }}
              />
            )}
          </ListItem>
        ))}
      </List>

      {/* Full-size image preview dialog */}
      <Dialog
        open={!!expandedImage}
        onClose={() => setExpandedImage(null)}
        maxWidth="lg"
      >
        {expandedImage && (
          <Box
            component="img"
            src={expandedImage}
            onClick={() => setExpandedImage(null)}
            sx={{ maxWidth: '90vw', maxHeight: '90vh', cursor: 'pointer' }}
          />
        )}
      </Dialog>
    </Box>
  );
}

export default DocumentList;
